/// <reference path="../types/api.d.js" />

import AMaterialRow from './a-material-row';

class MSearchResults {
	#categories = null;

	selectedTab = null;

	/** @param {HTMLElement} element */
	constructor(element) {
		this.element = element;
		this.resultsElement = element.querySelector('.m-search-results__results');
		this.quicklinksElement = element.querySelector('.m-search-results__quicklinks');
		this.noticeElement = element.querySelector('.m-search-results__notice');
		this.#categories = JSON.parse(element.dataset.categories);
		this.selectedTab = 'pages';

		this.resultsElement.addEventListener('click', (/** @type {PointerEvent} */ event) => {
			/** @type {HTMLElement} */
			const targetElement = event.target;
			const buttonElement = targetElement?.closest('button[role="tab"][aria-controls]');
			if (buttonElement && buttonElement.ariaDisabled !== 'true') {
				this.showTab(buttonElement.getAttribute('data-key'));
			}
		});
		this.resultsElement.addEventListener('keydown', (event) => {
			if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
				this.onTablistArrowKeydown(event);
			}
		});
	}

	onTablistArrowKeydown(event) {
		/** @type {HTMLElement} */
		const targetElement = event.target;
		const buttonElement = targetElement?.closest('button[role="tab"][aria-controls]');
		const tablistElement = buttonElement.parentNode;
		if (buttonElement) {
			buttonElement.setAttribute('tabindex', -1);
			let newFocusElement;
			if (event.key === 'ArrowRight') {
				newFocusElement = buttonElement.nextElementSibling ?? tablistElement.firstElementChild;
			} else if (event.key === 'ArrowLeft') {
				newFocusElement = buttonElement.previousElementSibling ?? tablistElement.lastElementChild;
			}

			newFocusElement.setAttribute('tabindex', 0);
			newFocusElement.focus();
		}
	}

	showTab(id) {
		const panelId = `panel-${id}`;
		const tabpanelElements = this.resultsElement.querySelectorAll('[role="tabpanel"]');
		const tabElements = this.resultsElement.querySelectorAll('[aria-controls]');

		tabpanelElements.forEach((tabpanelElement) => {
			tabpanelElement.toggleAttribute('hidden', tabpanelElement.id !== panelId);
		});
		tabElements.forEach((tabElement) => {
			tabElement.setAttribute('aria-selected', tabElement.getAttribute('aria-controls') === panelId);
		});

		this.selectedTab = id;
	}

	get isOpen() {
		return !this.element.hasAttribute('hidden');
	}

	show() {
		this.element.toggleAttribute('hidden', false);
	}

	hide() {
		this.element.toggleAttribute('hidden', true);
	}

	setMarginBlockStart(value) {
		this.element.style.setProperty('--margin-block-start', `${value}px`);
	}

	renderNotice(message, theme = null) {
		this.noticeElement.toggleAttribute('hidden', false);
		this.quicklinksElement.toggleAttribute('hidden', true);
		this.resultsElement.innerHTML = '';
		this.noticeElement.textContent = message;
		this.noticeElement.dataset.theme = theme;
	}

	/** @param {?SearchData} data */
	/** @param {?string} query */
	render(data = null, query = null) {
		let resultsHTML = '';
		let showQuicklinks = true;

		if (query === null) {
			showQuicklinks = true;
		} else if (data?.count === 0) {
			showQuicklinks = false;
		} else {
			showQuicklinks = false;

			const categoryKeys = Object.keys(this.#categories);

			if (!Object.keys(data.groups).includes(this.selectedTab)) {
				[this.selectedTab] = Object.keys(data.groups);
			}

			resultsHTML = `
				<div class="m-search-results__tabs" role="tablist" aria-label="Categories">
					${categoryKeys.map((groupKey) => `
						<button
							role="tab"
							id="tab-${groupKey}"
							data-key="${groupKey}"
							aria-selected="${this.selectedTab === groupKey ? 'true' : 'false'}"
							aria-controls="panel-${groupKey}"
							aria-disabled="${data?.groups[groupKey] ? 'false' : 'true'}"
							tabindex="${this.selectedTab === groupKey ? '0' : '-1'}"
						>
							${this.#categories[groupKey] ?? groupKey}
							<small>
								<span class="a-visually-hidden">,</span>
								${data?.groups[groupKey]?.length ?? 0}
								<span class="a-visually-hidden">results</span>
							</small>
						</button>
					`).join('')}
				</div>

				${categoryKeys.filter((_) => data?.groups[_]).map((groupKey) => `
					<div id="panel-${groupKey}" role="tabpanel" aria-labelledby="tab-${groupKey}" class="m-search-results__tab" ${this.selectedTab === groupKey ? '' : 'hidden'}>
						${groupKey === 'materials' ? `
							<div class="m-table" data-variant="products">
								<table class="m-table">
									<thead>
										<th>Name</th>
										<td>Particle Sizes</td>
										<td>Carbon Load</td>
										<td>Pore Size</td>
									</thead>
									<tbody>
										${data?.groups[groupKey].map((/** @type {SearchItem} */searchItem) => `
											<tr class="a-material-row">
												<th>
													<a href="${searchItem.url}">
														${searchItem.title}
													</a>
												</th>
												<td>${searchItem.particleSize}</td>
												<td>${searchItem.carbonLoad ?? ''}</td>
												<td>${searchItem.poreSize ?? ''}</td>
										`).join('')}
									</tbody>
								</table>
							</div>
						` : ''}
						${groupKey === 'applications' ? `
							<ul class="m-list-search-results">
								${data?.groups[groupKey].map((/** @type {SearchItem} */searchItem) => `
									<li>
										<a class="a-thumb-application" data-variant="small" href="${searchItem.url}">
											<strong class="a-thumb-application__title">${searchItem.title}</strong>
											<small class="a-thumb-application__variant">${searchItem.material}</small>
										</a>
									</li>
								`).join('')}
							</ul>
						` : ''}
						${groupKey === 'pages' ? `
							<ul class="m-list-search-results">
								${data?.groups[groupKey].map((/** @type {SearchItem} */searchItem) => `
									<li>
										<a class="a-thumb-page" href="${searchItem.url}">
											<strong>${searchItem.title}</strong>
											<em>${searchItem.breadcrumbs.join(' / ')}</em>
										</a>
									</li>
								`).join('')}
							</ul>
						` : ''}
					</div>
				`).join('')}
			`;
		}

		this.quicklinksElement.toggleAttribute('hidden', !showQuicklinks);
		this.noticeElement.toggleAttribute('hidden', true);
		this.resultsElement.innerHTML = resultsHTML;

		this.resultsElement.querySelectorAll('.a-material-row').forEach((element) => new AMaterialRow(element));
	}
}

document.querySelectorAll('.m-search-results[data-variant="standalone"').forEach((_) => new MSearchResults(_));

export default MSearchResults;
